import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { TopNavContext } from '../../../providers/TopNavProvider';
import { useDimensions } from '../../../hooks/useDimensions';
import { topNavOffsetClassesGetter } from '../../../helpers/topNavOffsetClasses';
import { ThirdNavLeftButtons, ThirdNavRightButtons } from './ThirdLevelNavigation';
import { useThirdNavChildren } from '../../../hooks/useThirdNavChildren';
import classNames from 'classnames';
import classes from './MediaLibraryPageLayout.module.scss';
import { FluidContentLayoutView } from '../../../components/containers/FluidContentLayout/FluidContentLayout.view';
import { MediaLibraryFilterMenu } from './MediaLibraryFilterMenu';
import { MediaGrid } from './MediaGrid';
import { Loader } from '../../../components/containers/Loader';
import { AddMediaSelectorModal } from './AddMediaSelectorModal';
import { AddMediaByUrlModal } from './AddMediaByUrlModal';
import { FindImagesModal } from './FindImagesModal';
import { MediaGridContext } from './MediaGrid/MediaGrid.view';
import { MediaLibraryViewProps } from './MediaLibrary.view';
import { FilterMenuContextType } from './MediaLibraryFilterMenu/MediaLibraryFilterMenu.view';
import { MediaItem } from '../../../models/media';
import { MediaTypePostEditorState, PostEditorContextEnum, PostEditorState } from '../../../models/post-editor';
import { AppContext } from '../../../providers/AppProvider';
import { InitialPostEditorState } from '../../PostEditor/states/initial';
import { MediaUploadStatus } from '../../../constants/media';
import { ImmutabilityContext } from '../../PostEditor/states/effects/customImmutabilityContext';
import { PostEditorMediaUploadFromThirdPartyAction } from '../../PostEditor/states/PostEditorActions';
import { showToast } from '../../../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../../../constants/apollo-errors';
import { getMediaProportion } from '../../../helpers/media';
import { DesignInCanvaModal } from './DesignInCanvaModal';
import { SourceType } from '../../../hooks/useMediaUpload';
import { MediaType, PostSource } from 'models/graphql';
import { setPostSource } from '../../PostEditor/helpers/setPostSource';
import { MediaLibraryEmptyPlaceholder } from './MediaLibraryEmptyPlaceholder';
import { ScrollContext } from '../../../providers/ScrollProvider';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type MediaLibraryPageLayoutViewProps = MediaLibraryViewProps & {
  gridContext: MediaGridContext;
};

const setMediaToInitialPostEditor = (media: MediaItem[]): PostEditorState => {
  const { update: updateImmutable } = ImmutabilityContext;
  return updateImmutable(InitialPostEditorState(), {
    postGroups: {
      0: {
        groupPostAttributes: {
          content: {
            media: {
              $set: media.map<MediaTypePostEditorState>(file => ({
                loading: false,
                url: file.url || '',
                id: file.id,
                gif: file.isGif,
                thumbnailUrl: file.thumbnailUrl || '',
                name: file.fileName,
                status: MediaUploadStatus.COMPLETED,
                type: file.type,
                proportions: file.proportion || getMediaProportion(file.width, file.height),
                width: file.width,
                height: file.height,
                length: file.length,
                transcoded: !!file.transcoded,
                externalUpload: false
              }))
            }
          }
        }
      }
    }
  });
};

export const MediaLibraryPageLayoutView = (props: MediaLibraryPageLayoutViewProps): JSX.Element => {
  const rightColRef = useRef<HTMLDivElement>(null);
  const topNavOffsetContext = useContext(TopNavContext);
  const { setOnScrollBottom } = useContext(ScrollContext);
  const { setPostEditorContext, selectedWorkspaceId, setSuspendedModalOpen } = useContext(AppContext);

  const { filters, dispatchFilters, showFilterModal, setShowFilterModal, setShowSelectionModal } = props;
  const { width } = useDimensions(rightColRef, 150);
  const topNavClasses = topNavOffsetClassesGetter(topNavOffsetContext.state.visibility, 0);

  const topNavChildren = useMemo(
    () => ({
      left: (
        <ThirdNavLeftButtons
          filters={filters}
          dispatchFilters={dispatchFilters}
          open={showFilterModal}
          setOpen={setShowFilterModal}
        />
      ),
      right: (
        <ThirdNavRightButtons
          onClick={() => {
            setShowSelectionModal(true);
          }}
        />
      )
    }),
    [filters, dispatchFilters, showFilterModal, setShowFilterModal, setShowSelectionModal]
  );

  useThirdNavChildren(topNavChildren);

  const rightColumnWrapperClass = classNames(classes.RightSideColumn);
  const leftColumnWrapperClass = classNames(
    classes.LeftSideWrapper,
    'cell',
    'show-for-xxlarge',
    topNavClasses.vheight,
    topNavClasses.top
  );
  const leftColumnClass = classNames(classes.LeftSideColumn, topNavClasses.vheight);
  const { isTemporallySuspended } = useSubscriptionStatus();
  const shareInPostAction = (media: MediaItem[]) => {
    if (isTemporallySuspended) {
      setSuspendedModalOpen(true);
      return;
    }
    const initialState = setPostSource(setMediaToInitialPostEditor(media), PostSource.IMPORT_MEDIA);
    setPostEditorContext({ type: PostEditorContextEnum.new, initialState });
  };

  const addToLibraryAndShareInPost = (media: MediaItem[]) => {
    if (!selectedWorkspaceId) {
      showToast({
        severity: 'error',
        detail: GENERIC_ERROR_MESSAGE,
        summary: 'Error'
      });
      return;
    }

    const payload: PostEditorMediaUploadFromThirdPartyAction['payload'] = {
      postGroupIndex: 0,
      customizeIndex: undefined,
      selectedWorkspaceId,
      queueUploads: props.queueUploads,
      media
    };
    setPostEditorContext({
      type: PostEditorContextEnum.new,
      initialState: setPostSource(InitialPostEditorState(), PostSource.IMPORT_MEDIA),
      payload: { type: 'mediaUploadFromThirdParty', payload }
    });
    props.setShowSelectionModal(false);
    props.setShowFindImagesModal(false);
  };

  const deleteFromLibraryAction = (media: MediaItem[]) =>
    props.deleteMediaHandler(
      media.map(media => ({ id: media.id, type: media.type })),
      false
    );

  const hasFilters = !!(props.filters.origin || props.filters.type || props.filters.proportion);

  const { fetchMoreMediaItemsHandler } = props;
  useEffect(() => {
    setOnScrollBottom(fetchMoreMediaItemsHandler);
  }, [fetchMoreMediaItemsHandler, setOnScrollBottom]);

  return (
    <>
      <FluidContentLayoutView>
        <div className={leftColumnWrapperClass}>
          <div className={leftColumnClass}>
            <MediaLibraryFilterMenu
              filters={props.filters}
              dispatchFilters={props.dispatchFilters}
              context={FilterMenuContextType.DROPDOWN}
            />
          </div>
        </div>
        <div className={rightColumnWrapperClass} ref={rightColRef}>
          {props.loading ? (
            <div className={classes.MediaLibraryLoaderWrapper}>
              <div className={classes.MediaLibraryLoaderContainer}>
                <Loader variation={'dark'} size={'large'} type={'inline'} />
              </div>
            </div>
          ) : props.mediaItems.length ? (
            <MediaGrid
              mediaItems={props.mediaItems}
              primaryButtonAction={shareInPostAction}
              primaryButtonTitle={'Share in Post'}
              secondaryButtonAction={deleteFromLibraryAction}
              secondaryButtonTitle={'Delete'}
              context={props.gridContext}
              parentWidth={width}
              selectedMediaItems={props.selectedMediaItems}
              setSelectedMediaItems={props.setSelectedMediaItems}
              setImagesIdsWithErrorSelected={props.setImagesIdsWithErrorSelected}
              imagesIdsWithErrorSelected={props.imagesIdsWithErrorSelected}
            />
          ) : (
            <MediaLibraryEmptyPlaceholder buttonAction={() => setShowSelectionModal(true)} hasFilters={hasFilters} />
          )}
          {props.loadingMore && (
            <div className={classes.LoadingMore}>
              <Loader variation={'dark'} size={'small'} type={'inline'} />
            </div>
          )}
        </div>
        {props.showSelectionModal && (
          <AddMediaSelectorModal
            onClose={() => props.setShowSelectionModal(false)}
            fileUploadHandler={props.fileUploadHandler}
            mediaByUrlClickHandler={() => props.setShowAddMediaByUrlModal(true)}
            findImagesClickHandler={() => props.setShowFindImagesModal(true)}
            designInCanvaClickHandler={() => props.setDesignInCanvaModal(true)}
          />
        )}
        {props.showAddMediaByUrlModal && (
          <AddMediaByUrlModal
            onClose={() => props.setShowAddMediaByUrlModal(false)}
            onSubmit={(url: string) => {
              props.fileUploadHandler([{ sourceType: SourceType.external, url, type: MediaType.PHOTO }]);
              props.setShowAddMediaByUrlModal(false);
            }}
          />
        )}
        {props.showFindImagesModal && (
          <FindImagesModal
            shareItemAction={addToLibraryAndShareInPost}
            context={MediaGridContext.FIND_IMAGES}
            onClose={() => props.setShowFindImagesModal(false)}
            fileUploadHandler={props.fileUploadHandler}
            setImagesIdsWithErrorSelected={props.setImagesIdsWithErrorSelected}
            imagesIdsWithErrorSelected={props.imagesIdsWithErrorSelected}
          />
        )}
        {props.showDesignInCanvaModal && (
          <DesignInCanvaModal
            onCloseHandler={() => props.setDesignInCanvaModal(false)}
            fileUploadHandlerPage={props.fileUploadHandler}
            publishLabel={'Add to Media Library'}
          />
        )}
      </FluidContentLayoutView>
    </>
  );
};
